import React, { useEffect, useState, useContext } from "react";
import WelcomeHeader from "./WelcomeHeader";
import AssetStatusPanel from "./AssetStatusPanel";
import AlarmsPanel from "./AlarmsPanel";
import PerformanceSummary from "./PerformanceSummary";
import DashboardChatbot from "./DashboardChatbot";
import axios from "axios";
import AppContext from "../../AppContext";
import {
  Cloud,
  CloudRain,
  CloudSun,
  Snowflake,
  Sun,
} from "lucide-react";
import { BASE_CHAT_URL } from "../../constant";

const Dashboard = () => {
  const [messages, setMessages] = useState([]);
  const [followUpOptions, setFollowUpOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rigCards, setRigCards] = useState([]);
  const [showAssetDetails, setShowAssetDetails] = useState(false);

  const { authCtx } = useContext(AppContext);

  const weatherIcons = {
    Sunny: Sun,
    Cloudy: Cloud,
    "Partly Cloudy": CloudSun,
    Rainy: CloudRain,
    Snowy: Snowflake,
  };

  const mockWeatherConditions = ["Sunny", "Cloudy", "Partly Cloudy", "Rainy"];

  const [dashboardData, setDashboardData] = useState({
    weather: { temp: 72, condition: "Sunny", icon: Sun },
    graphData: [],
    kpis: [],
    assets: [],
    alarms: [], // Will be populated from RAG backend
    hasGraphData: false,
  });
  const [selectedRig, setSelectedRig] = useState(null);

  useEffect(() => {
    const weatherMockIndex = Math.floor(
      Math.random() * mockWeatherConditions.length
    );
    setDashboardData((pre) => ({
      ...pre,
      weather: {
        temp: Math.floor(Math.random() * 11) + 30,
        condition: mockWeatherConditions[weatherMockIndex],
        icon: weatherIcons[mockWeatherConditions[weatherMockIndex]],
      },
    }));
  }, []);  const handleApiCall = async (userInput = null) => {
    setIsLoading(true);
    if (userInput) {
      // Detect rig selection in user input (improved pattern matching)
      const rigMatch = userInput.match(/rig\s*(\d+)/i);
      if (rigMatch) {
        setSelectedRig(`RIG ${rigMatch[1]}`);
        setShowAssetDetails(true); // Show asset details when drilling into a specific rig
      }
      
      const userMessage = {
        id: Date.now(),
        type: "user",
        content: userInput,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
    }
    try {
      const payload = {
        ...(userInput && { query: userInput }),
        domain_id: authCtx.profile.companyId,
        session_id: authCtx.profile.id,
        user: authCtx.profile.name,
        is_initial_load: !userInput,
      };

      const response = await axios.post(
        `${BASE_CHAT_URL}/api/query`,
        payload
      );      if (response.status === 200) {        const botResponse = {
          id: Date.now() + 1,
          type: "bot",
          content: response.data.answer,
          graph: response.data.chartData,
          timestamp: new Date(),
        };setMessages((prev) => [...prev, botResponse]);

        // Show asset details only when user asks follow-up questions
        if (userInput) {
          setShowAssetDetails(true);
        }        // Process cards data for Rigs
        if (response.data.cards) {
          setRigCards(response.data.cards);
          
          // Detect if this is a rig drill-down based on user input or single rig returned
          if (userInput) {
            const rigMatch = userInput.match(/rig\s*(\d+)/i);
            if (rigMatch) {
              const selectedRigName = `RIG ${rigMatch[1]}`;
              setSelectedRig(selectedRigName);
              setShowAssetDetails(true);
              
              // Filter the cards to show only the selected rig
              const filteredCards = response.data.cards.filter(rig => rig.title === selectedRigName);
              setRigCards(filteredCards);
            }
          } else if (!userInput) {
            // Reset selection on initial load
            setSelectedRig(null);
            setShowAssetDetails(false);
          }
            // Extract alarms from rig data
          const extractedAlarms = [];
          response.data.cards.forEach((rig) => {
            rig.children?.forEach((asset) => {
              if (asset.status === "Critical" || asset.status === "Warning") {
                extractedAlarms.push({
                  id: `${rig.title}-${asset.name}`,
                  type: asset.status.toLowerCase(),
                  asset: asset.name,
                  message: asset.issue || "Issue detected",
                  timestamp: "Active",
                  rig: rig.title
                });
              }
            });
          });
          
          // Filter alarms by selected rig if drilling down
          setDashboardData(prev => {
            const currentSelectedRig = userInput && userInput.match(/rig\s*(\d+)/i) 
              ? `RIG ${userInput.match(/rig\s*(\d+)/i)[1]}` 
              : selectedRig;
              
            const filteredAlarms = currentSelectedRig 
              ? extractedAlarms.filter(alarm => alarm.rig === currentSelectedRig)
              : extractedAlarms;
            
            return {
              ...prev,
              alarms: filteredAlarms.length > 0 ? filteredAlarms : 
                     (currentSelectedRig ? prev.alarms.filter(alarm => alarm.rig === currentSelectedRig) : prev.alarms)
            };
          });

          // Extract assets from rig data for AssetStatusPanel
          const extractedAssets = [];
          response.data.cards.forEach((rig) => {
            rig.children?.forEach((asset) => {
              extractedAssets.push({
                id: asset.name.split(' ').pop(), // Get model number
                name: asset.name,
                type: asset.name.includes('Generator') ? 'Generator' : 
                      asset.name.includes('Transformer') ? 'Transformer' : 'Storage Tank',
                status: asset.status,
                health: asset.status === 'Operational' ? 85 : 
                       asset.status === 'Warning' ? 65 : 30,
                performance: asset.status === 'Operational' ? 92 : 
                            asset.status === 'Warning' ? 75 : 40,
                temp: asset.name.includes('Generator') ? 76 : 
                      asset.name.includes('Transformer') ? 44 : 35,
                load: asset.status === 'Critical' ? 0 : 
                      asset.status === 'Warning' ? 75 : 85,
                trend: asset.status === 'Critical' ? 'down' : 'up',
                rig: rig.title
              });
            });
          });
            setDashboardData(prev => ({
            ...prev,
            assets: extractedAssets.slice(0, 4) // Show first 4 assets
          }));
        }        // Process rigCards for AssetStatusPanel
        if (response.data.rigCards) {
          console.log("Received rigCards from API:", response.data.rigCards);
          setDashboardData(prev => ({
            ...prev,
            rigCardsForPanel: response.data.rigCards
          }));
        } else {
          console.log("No rigCards received from API");
        }// Process chart data if present
        if (response.data.chartData) {
          console.log("Processing chartData:", response.data.chartData);
          // Convert the new chartData format to the format expected by PerformanceSummary
          let chartDataForPerformance = [];
          let chartType = "column"; // Default chart type
            // Priority order: specificAssetAlarms (specific asset) > assetAlarms (drill-down) > rigAlarms (overview) > alarmTypes
          if (response.data.chartData.specificAssetAlarms) {
            // Specific asset view: show alarm breakdown for a specific asset
            const specificData = response.data.chartData.specificAssetAlarms;
            chartType = specificData.chartType || "column";
            chartDataForPerformance = specificData.labels.map((label, index) => ({
              label: label,
              value: specificData.datasets[0].data[index],
              x: index + 1,
              color: specificData.datasets[0].backgroundColor[index],
              metricName: specificData.title || "Asset Alarm Breakdown",
              chartType: chartType
            }));
            console.log("Using specificAssetAlarms data for specific asset view");
          } else if (response.data.chartData.assetAlarms) {
            // Drill-down view: show asset alarms for specific rig
            const assetData = response.data.chartData.assetAlarms;
            chartType = assetData.chartType || "column";
            chartDataForPerformance = assetData.labels.map((label, index) => ({
              label: label,
              value: assetData.datasets[0].data[index],
              x: index + 1,
              color: assetData.datasets[0].backgroundColor[index],
              metricName: assetData.title || "Asset Alarms",
              chartType: chartType
            }));
            console.log("Using assetAlarms data for drill-down view");
          } else if (response.data.chartData.rigAlarms) {
            // Overview: show rig comparison
            const rigData = response.data.chartData.rigAlarms;
            chartType = rigData.chartType || "column";
            chartDataForPerformance = rigData.labels.map((label, index) => ({
              label: label,
              value: rigData.datasets[0].data[index],
              x: index + 1,
              color: rigData.datasets[0].backgroundColor[index],
              metricName: rigData.title || "Critical Alarms",
              chartType: chartType
            }));
            console.log("Using rigAlarms data for overview");
          } else if (response.data.chartData.alarmTypes) {
            // Alternative: show alarm types
            const alarmData = response.data.chartData.alarmTypes;
            chartType = alarmData.chartType || "column";
            chartDataForPerformance = alarmData.labels.map((label, index) => ({
              label: label,
              value: alarmData.datasets[0].data[index],
              x: index + 1,
              color: alarmData.datasets[0].backgroundColor[index],
              metricName: alarmData.title || "Alarm Types",
              chartType: chartType
            }));
            console.log("Using alarmTypes data");
          }
          
          console.log("ChartDataForPerformance:", chartDataForPerformance);
          console.log("Selected chart type:", chartType);
          
          setDashboardData((prev) => ({
            ...prev,
            graphData: chartDataForPerformance,
            hasGraphData: chartDataForPerformance.length > 0,
            chartData: response.data.chartData, // Store original chart data for other uses
            chartType: chartType // Store the chart type
          }));
        }// Process graph data if present (legacy format) - only if no chartData
        if (response.data.graph?.data && !response.data.chartData) {
          setDashboardData((prev) => {
            const graphData = response.data.graph.data.map((item, index) => {
              const isTimestamp = !isNaN(Date.parse(item.label));
              return {
                ...item,
                x: isTimestamp ? new Date(item.label) : index + 1,
                timestamp: isTimestamp ? new Date(item.label) : new Date(),
                label: isTimestamp
                  ? new Date(item.label).toLocaleDateString()
                  : item.label,
              };
            });

            const newKpis = processKPIs(graphData);

            return {
              ...prev,
              graphData,
              kpis: newKpis,
              hasGraphData: true, // Add flag to track graph availability
            };
          });
        }// Process KPIs if present
        if (response.data.kpis) {
          setDashboardData((prev) => ({
            ...prev,
            kpis: response.data.kpis
          }));
        }

        // Process follow-up questions
        if (response.data.follow_up?.length > 0) {
          setFollowUpOptions({
            question: "What would you like to know next?",
            options: response.data.follow_up
          });
        } else {
          setFollowUpOptions(null);
        }
      } else {
        throw new Error("API response was not successful");
      }
    } catch (error) {
      console.error("Error calling API:", error);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: "bot",
          content:
            "I'm having trouble processing your request. Please try again.",
          error: true,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const processKPIs = (data) => {
    if (!data || data.length < 2) return [];

    const getPercentageChange = (current, previous) => {
      if (!previous) return 0;
      return ((current - previous) / previous) * 100;
    };

    const metrics = {};
    const latestData = data[data.length - 1];
    const previousData = data[data.length - 2];

    Object.keys(latestData).forEach((key) => {
      if (typeof latestData[key] === "number" && key !== "timestamp") {
        const currentValue = latestData[key];
        const previousValue = previousData[key];
        const change = getPercentageChange(currentValue, previousValue);

        metrics[key] = {
          value: `${currentValue.toFixed(1)}${
            key.toLowerCase().includes("percent") ? "%" : ""
          }`,
          change: parseFloat(change.toFixed(1)),
          isPositive: change >= 0,
        };
      }
    });

    return Object.entries(metrics).map(([key, value]) => ({
      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
      ...value,
    }));
  };

  useEffect(() => {
    handleApiCall();
  }, []);

  return (
    <div className="flex h-full">
      {/* Main Content - 70% */}
      <div className="flex-1 h-full overflow-y-auto p-4 space-y-6">        <WelcomeHeader weather={dashboardData.weather} />        <div className="grid grid-cols-12 grid-rows-2 gap-6">          {/* Asset Status Panel - 35% of available width */}
          <div className="col-span-12 lg:col-span-4">            <AssetStatusPanel 
              assets={dashboardData.assets} 
              rigCards={dashboardData.rigCardsForPanel || rigCards} 
              showDetails={showAssetDetails}
              selectedRig={selectedRig}
            />
            {console.log("AssetStatusPanel rigCardsForPanel:", dashboardData.rigCardsForPanel)}
            {console.log("AssetStatusPanel fallback rigCards:", rigCards)}
          </div>          {/* Performance Summary - 65% of available width */}
          <div className="col-span-12 lg:col-span-8">
            {console.log("Dashboard hasGraphData:", dashboardData.hasGraphData)}
            {console.log("Dashboard graphData:", dashboardData.graphData)}
            <PerformanceSummary
              data={dashboardData.hasGraphData ? dashboardData.graphData : []}
              kpis={dashboardData.kpis}
              rigName={selectedRig}
              showDefault={!dashboardData.hasGraphData}
              chartType={dashboardData.chartType}
            />
          </div>

          {/* <div className="col-span-12 w-full">
            <AlarmsPanel alarms={dashboardData.alarms} />
          </div> */}
        </div>
      </div>
      
      {/* Asset Intelligence Panel - 30% */}
      <div className="w-[30%] border-l border-gray-800 bg-gray-900">
        <div className="h-full p-4">
          <DashboardChatbot
            messages={messages}
            setMessages={setMessages}
            followUpOptions={followUpOptions}
            isLoading={isLoading}
            onSendMessage={handleApiCall}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
